/**
 * FLAVORS: the @protomaps/basemaps layers for a mood, with the brand's label policy (owner B2).
 *
 *   basemapLayers(mood, source, { layout })  → LayerSpecification[] (Protomaps order, policy applied)
 *
 * Colours come only from MOODS[mood].flavor (src/map/style/moods.ts). What this file adds:
 *   - §D.9 label policy: keep places_locality (≥ 8, z8 12 → z14 15, no icon), places_subplace (≥ 12, 11.5),
 *     water_label_lakes and water_waterway_label (≥ 11, italic), roads_labels_major (≥ 13). Everything else
 *     that draws text or asks for a sprite is dropped.
 *   - No sprite anywhere: icon-* layout keys are stripped (the style has no `sprite`).
 *   - §D.12 kiosk text: text-size × 1.2 and text-halo-width + 0.5 on every kept label.
 *
 * Pure (no maplibre import): the tests and the style builder share it.
 */
import { layers as protomapsLayers } from "@protomaps/basemaps";
import type { LayerSpecification } from "maplibre-gl";
import type { Layout, Mood } from "@/contracts/store";
import { MOODS } from "./moods";

/** Layers that ask for the sprite or carry text the map does not want (§D.9). */
export const DROPPED_LAYERS: readonly string[] = [
  "pois",
  "roads_shields",
  "roads_oneway",
  "roads_labels_minor",
  "address_label",
  "places_region",
  "places_country",
  "earth_label_islands",
  "water_label_ocean", // not in the keep list: the sea lies outside every framed view
];

/** Kept basemap labels and their zoom floors (§D.9). */
export const KEPT_LABELS: Readonly<Record<string, { minzoom: number }>> = {
  places_locality: { minzoom: 8 },
  places_subplace: { minzoom: 12 },
  water_label_lakes: { minzoom: 11 },
  water_waterway_label: { minzoom: 11 },
  roads_labels_major: { minzoom: 13 },
};

/**
 * OSM spellings of the corridors this app draws and names itself (roads.geojson, D4 notes). Their basemap
 * labels are dropped so one road is not spelled two ways on one screen ("Lohgaon - Wagholi Road" beside
 * the app's "Lohegaon–Wagholi Road", QA L3-17). Same-spelled names ("Nagar Road") keep their label.
 */
export const CORRIDOR_OSM_NAMES: readonly string[] = [
  "Lohgaon - Wagholi Road",
  "Lohgaon - Wagholi New Road",
  "Lohgaon - Wagholi Old Road",
  "Lohegaon - Wagholi Road",
  "Mumbai - Pune Expressway",
  "Mumbai - Pune Expressway (Toll Road)",
  "Mumbai - Pune Expressway Missing Link",
  "Pune Nashik Road",
  "Pune Nashik Rd",
  "Pune - Nashik Rd",
  "Pune Saswad Road",
];

/** Text factor and halo addition by layout (§D.12). */
export function textScale(layout: Layout | undefined): { k: number; halo: number } {
  return layout === "kiosk" ? { k: 1.2, halo: 0.5 } : { k: 1, halo: 0 };
}

type Expr = unknown;
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Maps the OUTPUTS of a (possibly zoom-driven) value: numbers directly, the stop outputs of a top-level
 * `interpolate` / `step`, and data expressions via `wrap`. A zoom expression cannot be nested inside
 * arithmetic, so the outputs are rewritten instead of wrapping the whole value.
 */
export function mapOutputs(v: Expr, num: (n: number) => number, wrap: (e: Expr) => Expr): Expr {
  if (typeof v === "number") return round2(num(v));
  if (!Array.isArray(v)) return v;
  const head = v[0];
  if (head === "interpolate" || head === "interpolate-hcl" || head === "interpolate-lab") {
    const out: unknown[] = v.slice(0, 3);
    for (let i = 3; i < v.length; i += 2) out.push(v[i], mapOutputs(v[i + 1], num, wrap));
    return out;
  }
  if (head === "step") {
    const out: unknown[] = [v[0], v[1], mapOutputs(v[2], num, wrap)];
    for (let i = 3; i < v.length; i += 2) out.push(v[i], mapOutputs(v[i + 1], num, wrap));
    return out;
  }
  return wrap(v);
}

export const scaleValue = (v: Expr, k: number): Expr => (k === 1 ? v : mapOutputs(v, (n) => n * k, (e) => ["*", k, e]));
export const addValue = (v: Expr, a: number): Expr => (a === 0 ? v : mapOutputs(v, (n) => n + a, (e) => ["+", a, e]));

type AnyLayer = LayerSpecification & { layout?: Record<string, unknown>; paint?: Record<string, unknown> };

function stripIcons(layout: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!layout) return layout;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(layout)) if (!k.startsWith("icon-")) out[k] = v;
  return out;
}

/**
 * The flavor writes roads_labels_major's filter in the legacy form (["in", "kind", "highway", "major_road"]),
 * which cannot sit inside an expression ["all", …]. This rewrites that one shape as an expression; any
 * other filter is assumed to be an expression already.
 */
function legacyKindFilter(f: unknown): unknown {
  if (Array.isArray(f) && f[0] === "in" && typeof f[1] === "string" && f.slice(2).every((x) => typeof x === "string")) {
    return ["match", ["get", f[1]], f.slice(2), true, false];
  }
  return f;
}

/** Applies §D.9 overrides and §D.12 scaling to one kept label layer. */
function labelPolicy(l: AnyLayer, layout: Layout | undefined): AnyLayer {
  const { k, halo } = textScale(layout);
  const lay: Record<string, unknown> = { ...(stripIcons(l.layout) ?? {}) };
  const paint: Record<string, unknown> = { ...(l.paint ?? {}) };
  switch (l.id) {
    case "places_locality":
      lay["text-size"] = ["interpolate", ["linear"], ["zoom"], 8, 12, 14, 15];
      break;
    case "places_subplace":
      lay["text-size"] = 11.5;
      break;
    default:
      break;
  }
  let filter = (l as { filter?: unknown }).filter;
  if (l.id === "roads_labels_major") {
    const notCorridor = ["!", ["in", ["coalesce", ["get", "name"], ""], ["literal", CORRIDOR_OSM_NAMES]]];
    filter = filter ? ["all", legacyKindFilter(filter), notCorridor] : notCorridor;
  }
  if (lay["text-size"] !== undefined) lay["text-size"] = scaleValue(lay["text-size"], k);
  else lay["text-size"] = scaleValue(16, k); // MapLibre's default size, made explicit so kiosk scales it too
  paint["text-halo-width"] = addValue(paint["text-halo-width"] ?? 0, halo);
  const minzoom = Math.max(l.minzoom ?? 0, KEPT_LABELS[l.id]?.minzoom ?? 0);
  const out = { ...l, layout: lay, paint, ...(filter ? { filter } : {}) } as AnyLayer;
  // The flavor emits water_waterway_label at 13; the spec lowers it to 11, so the floor is set, not max'd.
  out.minzoom = l.id === "water_waterway_label" ? KEPT_LABELS[l.id].minzoom : minzoom;
  return out;
}

export interface FlavorLayerOptions {
  layout?: Layout;
  /** Prefix for layer ids (the underlay uses "ld-under-"). Policy lookups use the Protomaps id. */
  idPrefix?: string;
}

/** Protomaps layer ids of the text layers this app keeps, in the same order the flavor emits them. */
export function isKeptLabel(protomapsId: string): boolean {
  return Object.prototype.hasOwnProperty.call(KEPT_LABELS, protomapsId);
}

/**
 * The @protomaps/basemaps layers for a mood with the label policy, no sprite references and the
 * layout's text scale. Ids are Protomaps ids (optionally prefixed).
 */
export function basemapLayers(mood: Mood, source: string, o: FlavorLayerOptions = {}): LayerSpecification[] {
  const flavor = MOODS[mood].flavor;
  const raw = protomapsLayers(source, flavor, { lang: "en" }) as AnyLayer[];
  const out: LayerSpecification[] = [];
  for (const l of raw) {
    if (DROPPED_LAYERS.includes(l.id)) continue;
    let next: AnyLayer = l;
    if (l.type === "symbol") {
      if (!isKeptLabel(l.id)) continue; // a text layer not on the keep list
      next = labelPolicy(l, o.layout);
    } else if (l.layout && Object.keys(l.layout).some((k) => k.startsWith("icon-"))) {
      next = { ...l, layout: stripIcons(l.layout) } as AnyLayer;
    }
    if (o.idPrefix) next = { ...next, id: `${o.idPrefix}${l.id}` } as AnyLayer;
    out.push(next);
  }
  return out;
}

/** The Protomaps id behind a (possibly prefixed) layer id. */
export const protomapsIdOf = (id: string, prefix = "ld-under-"): string => (id.startsWith(prefix) ? id.slice(prefix.length) : id);
